/**
 * In-boundary Supabase shim for the S/4-hosted (Option A) build.
 *
 * GOAL: the S/4-hosted timesheet must be **fully self-contained in S/4 HANA**
 * with ZERO Supabase reliance (only the AI proxy egresses). The ~100 admin /
 * compliance services call `supabase.from(<table>)…` (a PostgREST query builder).
 * Rather than rewrite each, this shim implements the subset of that builder they
 * use and backs it with a same-origin ABAP config store — the ICF handler
 * `ZCL_M12_CFG_STORE` at `/sap/bc/zm12cfg` over table `ZTM12_TS_CFG` (generic
 * key-value: tbl + org_id + row_id → JSON payload).
 *
 *   GET  /sap/bc/zm12cfg?tbl=<t>&org=<o>              → { rows: [ <row json>, … ] }
 *   POST /sap/bc/zm12cfg  { op:'upsert', tbl,org,id, payload:'<row json>' }
 *   POST /sap/bc/zm12cfg  { op:'delete', tbl,org,id }
 *
 * Reads pull every row for (tbl, org) and apply the remaining filters / ordering
 * / range CLIENT-SIDE — fine because these are small CONFIG tables (charge-object
 * types, policies, custom rules, retention config…). Bulk SAP data (WBS, orders,
 * personnel, actuals, time) is NEVER stored here — it is read live from standard
 * SAP CDS/tables. Writes upsert/delete one row per `id`.
 *
 * Only wired in when `VITE_TS_BACKEND==='cats'` and Supabase is not configured
 * (see lib/supabase.ts). The live Vercel build is untouched.
 */

const CFG_BASE = '/sap/bc/zm12cfg'

/**
 * The shop floor clock is NOT config. Its three tables are read live from the
 * manufacturing time clock service, with the caller's worker / time / voided
 * filters pushed down. Loaded lazily so the shim stays free of the timesheet
 * module until a clock table is actually read.
 */
const CLOCK_TABLES: ReadonlySet<string> = new Set(['assignment_events', 'clock_segments', 'presence_events'])
const CLOCK_READ_ONLY =
  'The shop floor clock tables are written through the clock service on the SAP-hosted build, not directly.'

/**
 * Shifts are NOT config either: the ABAP clock engine checks a shift clock in
 * against the shift master, so the master is a real SAP table behind the
 * timesheet service. Reads and writes of `shifts` go there (see
 * `sapShiftTable`), in the same row shape.
 */
const SHIFT_TABLE = 'shifts'
const sapShifts = () => import('@/modules/timesheet/services/sapShiftTable')

// ── HTTP to the ABAP config store ────────────────────────────────────────────

async function cfgList(tbl: string, org: string): Promise<Record<string, unknown>[]> {
  const url = `${CFG_BASE}?tbl=${encodeURIComponent(tbl)}&org=${encodeURIComponent(org)}`
  const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'include' })
  if (!res.ok) throw new Error(`[cfg-store] GET ${tbl} failed (${res.status})`)
  const body = (await res.json()) as { rows?: Record<string, unknown>[] }
  return body.rows ?? []
}

async function cfgWrite(payload: Record<string, unknown>): Promise<void> {
  const res = await fetch(CFG_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    let detail = ''
    try { detail = `: ${(await res.text()).slice(0, 200)}` } catch { /* ignore */ }
    throw new Error(`[cfg-store] ${payload.op} ${payload.tbl} failed (${res.status})${detail}`)
  }
}

// ── PostgREST-ish result + filter types ──────────────────────────────────────

interface PgResult<T = unknown> { data: T; error: { message: string } | null; count?: number | null }

type Op = 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'is' | 'not' | 'like' | 'ilike' | 'contains'
interface Filter { col: string; op: Op; val: unknown; not?: boolean }

function getField(row: Record<string, unknown>, col: string): unknown {
  return row[col]
}

function matchOne(row: Record<string, unknown>, f: Filter): boolean {
  const v = getField(row, f.col)
  let res: boolean
  switch (f.op) {
    case 'eq':  res = v === f.val || String(v) === String(f.val); break
    case 'neq': res = !(v === f.val || String(v) === String(f.val)); break
    case 'gt':  res = (v as number) > (f.val as number); break
    case 'gte': res = (v as number) >= (f.val as number); break
    case 'lt':  res = (v as number) < (f.val as number); break
    case 'lte': res = (v as number) <= (f.val as number); break
    case 'in':  res = Array.isArray(f.val) && (f.val as unknown[]).some((x) => x === v || String(x) === String(v)); break
    case 'is':  res = f.val === null ? (v === null || v === undefined) : v === f.val; break
    case 'not': res = !(f.val === null ? (v === null || v === undefined) : v === f.val); break
    case 'like':  res = likeMatch(String(v ?? ''), String(f.val), false); break
    case 'ilike': res = likeMatch(String(v ?? ''), String(f.val), true); break
    case 'contains': res = Array.isArray(v) && Array.isArray(f.val) && (f.val as unknown[]).every((x) => (v as unknown[]).includes(x)); break
    default: res = true
  }
  return f.not ? !res : res
}

function likeMatch(value: string, pattern: string, ci: boolean): boolean {
  const re = new RegExp('^' + pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/%/g, '.*').replace(/_/g, '.') + '$', ci ? 'i' : '')
  return re.test(value)
}

// ── Query builder ────────────────────────────────────────────────────────────

type Mode = 'select' | 'insert' | 'upsert' | 'update' | 'delete'

class CfgQuery<T = unknown> implements PromiseLike<PgResult<T>> {
  private filters: Filter[] = []
  private orders: { col: string; asc: boolean }[] = []
  private _limit: number | null = null
  private _range: [number, number] | null = null
  private mode: Mode = 'select'
  private values: Record<string, unknown>[] = []
  private wantRows = false
  private _single = false
  private _maybeSingle = false
  private _count: 'exact' | null = null
  private _head = false

  /**
   * `legacyShifts` reads shifts that were set up before they lived in SAP: the
   * in-boundary config store on the BSP build, the app database on the hosted
   * app. The first read that finds SAP empty carries them over.
   */
  constructor(private tbl: string, private legacyShifts?: () => Promise<Record<string, unknown>[]>) {}

  // terminal-ish modifiers ---------------------------------------------------
  select(_cols?: string, opts?: { count?: 'exact'; head?: boolean }): this {
    if (this.mode === 'select') this.mode = 'select'
    this.wantRows = true
    if (opts?.count) this._count = opts.count
    if (opts?.head) this._head = true
    return this
  }
  insert(v: Record<string, unknown> | Record<string, unknown>[]): this { this.mode = 'insert'; this.values = Array.isArray(v) ? v : [v]; return this }
  upsert(v: Record<string, unknown> | Record<string, unknown>[], _opts?: { onConflict?: string }): this { this.mode = 'upsert'; this.values = Array.isArray(v) ? v : [v]; return this }
  update(v: Record<string, unknown>): this { this.mode = 'update'; this.values = [v]; return this }
  delete(): this { this.mode = 'delete'; return this }

  // filters ------------------------------------------------------------------
  eq(col: string, val: unknown): this { this.filters.push({ col, op: 'eq', val }); return this }
  neq(col: string, val: unknown): this { this.filters.push({ col, op: 'neq', val }); return this }
  gt(col: string, val: unknown): this { this.filters.push({ col, op: 'gt', val }); return this }
  gte(col: string, val: unknown): this { this.filters.push({ col, op: 'gte', val }); return this }
  lt(col: string, val: unknown): this { this.filters.push({ col, op: 'lt', val }); return this }
  lte(col: string, val: unknown): this { this.filters.push({ col, op: 'lte', val }); return this }
  in(col: string, val: unknown[]): this { this.filters.push({ col, op: 'in', val }); return this }
  is(col: string, val: unknown): this { this.filters.push({ col, op: 'is', val }); return this }
  not(col: string, _op: string, val: unknown): this { this.filters.push({ col, op: 'is', val, not: true }); return this }
  like(col: string, val: string): this { this.filters.push({ col, op: 'like', val }); return this }
  ilike(col: string, val: string): this { this.filters.push({ col, op: 'ilike', val }); return this }
  contains(col: string, val: unknown): this { this.filters.push({ col, op: 'contains', val }); return this }

  order(col: string, opts?: { ascending?: boolean }): this { this.orders.push({ col, asc: opts?.ascending !== false }); return this }
  limit(n: number): this { this._limit = n; return this }
  range(from: number, to: number): this { this._range = [from, to]; return this }
  single(): this { this._single = true; this.wantRows = true; return this }
  maybeSingle(): this { this._maybeSingle = true; this.wantRows = true; return this }

  // org scope: the config store keys rows by org_id — take it from an eq filter
  // (reads) or the row (writes). Missing → '' (a global/un-scoped config table).
  private orgOf(row?: Record<string, unknown>): string {
    if (row && row.org_id != null) return String(row.org_id)
    const f = this.filters.find((x) => x.col === 'org_id' && x.op === 'eq')
    return f ? String(f.val) : ''
  }

  private rowIdOf(row: Record<string, unknown>): string {
    if (row.id != null) return String(row.id)
    // Fall back to a deterministic composite of the non-org key-ish fields.
    const parts = Object.entries(row)
      .filter(([k]) => k !== 'org_id' && /(_id|key|name|code)$/.test(k))
      .map(([, v]) => String(v))
    return parts.join('|') || JSON.stringify(row).slice(0, 100)
  }

  private applyReadShaping(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    let out = rows.filter((r) => this.filters.every((f) => matchOne(r, f)))
    for (const o of [...this.orders].reverse()) {
      out.sort((a, b) => {
        const av = a[o.col] as never, bv = b[o.col] as never
        if (av === bv) return 0
        return (av < bv ? -1 : 1) * (o.asc ? 1 : -1)
      })
    }
    if (this._range) out = out.slice(this._range[0], this._range[1] + 1)
    if (this._limit != null) out = out.slice(0, this._limit)
    return out
  }

  /** Rows for a read: the SAP clock service for a clock table, else the config store. */
  private async readRows(): Promise<Record<string, unknown>[]> {
    if (CLOCK_TABLES.has(this.tbl)) {
      const { readSapClockTable, isSapClockTable } = await import('@/modules/timesheet/services/sapClockTables')
      if (isSapClockTable(this.tbl)) return readSapClockTable(this.tbl, this.filters)
    }
    if (this.tbl === SHIFT_TABLE) return this.readShifts()
    return cfgList(this.tbl, this.orgOf())
  }

  /**
   * Shifts from SAP. Shifts set up before they moved into SAP still sit in the
   * config store; the first read that finds SAP empty carries them over with
   * their ids, so the break and working-day calendars that point at them keep
   * working. SAP only lets a supervisor or administrator write shifts, so for
   * anyone else the carry-over is skipped and tried again on a later read.
   */
  private async readShifts(): Promise<Record<string, unknown>[]> {
    const sap = await sapShifts()
    const rows = await sap.readSapShiftRows()
    if (rows.length > 0) return rows
    const legacy = await (this.legacyShifts ? this.legacyShifts() : cfgList(SHIFT_TABLE, this.orgOf())).catch(() => [])
    if (legacy.length === 0) return rows
    try {
      for (const row of legacy) await sap.upsertSapShiftRow(row, new Set())
    } catch (e) {
      console.warn('[cfg-store] shifts could not be carried over to SAP yet', e)
    }
    return sap.readSapShiftRows()
  }

  /** Writes to `shifts`, sent to SAP instead of the config store. */
  private async writeShifts(): Promise<PgResult<T>> {
    const sap = await sapShifts()
    const all = await sap.readSapShiftRows()
    const ids = new Set(all.map((r) => String(r.id)))
    if (this.mode === 'insert' || this.mode === 'upsert') {
      const written: Record<string, unknown>[] = []
      for (const row of this.values) {
        const saved = await sap.upsertSapShiftRow(row, ids)
        ids.add(String(saved.id))
        written.push(saved)
      }
      if (this.wantRows && (this._single || this._maybeSingle)) return { data: (written[0] ?? null) as unknown as T, error: null }
      return { data: (this.wantRows ? written : null) as unknown as T, error: null }
    }
    const target = all.filter((r) => this.filters.every((f) => matchOne(r, f)))
    if (this.mode === 'update') {
      const patch = this.values[0] ?? {}
      const updated: Record<string, unknown>[] = []
      for (const row of target) updated.push(await sap.upsertSapShiftRow({ ...patch, id: row.id }, ids))
      return { data: (this.wantRows ? updated : null) as unknown as T, error: null }
    }
    for (const row of target) await sap.deleteSapShiftRow(String(row.id))
    return { data: null as unknown as T, error: null }
  }

  private async run(): Promise<PgResult<T>> {
    try {
      if (this.mode === 'select') {
        const all = await this.readRows()
        const shaped = this.applyReadShaping(all)
        if (this._head) return { data: null as unknown as T, error: null, count: this.filters.length ? all.filter((r) => this.filters.every((f) => matchOne(r, f))).length : all.length }
        if (this._single) return { data: (shaped[0] ?? null) as unknown as T, error: shaped[0] ? null : { message: 'No rows found' } }
        if (this._maybeSingle) return { data: (shaped[0] ?? null) as unknown as T, error: null }
        return { data: shaped as unknown as T, error: null, count: this._count ? shaped.length : undefined }
      }

      // A direct write to a clock table would land in the config store, where
      // nothing reads it back. Refuse loudly instead of losing the row.
      if (CLOCK_TABLES.has(this.tbl)) throw new Error(CLOCK_READ_ONLY)
      if (this.tbl === SHIFT_TABLE) return await this.writeShifts()

      if (this.mode === 'insert' || this.mode === 'upsert') {
        for (const row of this.values) {
          await cfgWrite({ op: 'upsert', tbl: this.tbl, org: this.orgOf(row), id: this.rowIdOf(row), payload: JSON.stringify(row) })
        }
        const data = this.wantRows ? this.values : null
        if (this.wantRows && (this._single || this._maybeSingle)) return { data: (this.values[0] ?? null) as unknown as T, error: null }
        return { data: data as unknown as T, error: null }
      }

      if (this.mode === 'update') {
        const patch = this.values[0] ?? {}
        const all = await cfgList(this.tbl, this.orgOf())
        const target = all.filter((r) => this.filters.every((f) => matchOne(r, f)))
        const updated: Record<string, unknown>[] = []
        for (const row of target) {
          const merged = { ...row, ...patch }
          await cfgWrite({ op: 'upsert', tbl: this.tbl, org: this.orgOf(merged), id: this.rowIdOf(merged), payload: JSON.stringify(merged) })
          updated.push(merged)
        }
        return { data: (this.wantRows ? updated : null) as unknown as T, error: null }
      }

      // delete
      const all = await cfgList(this.tbl, this.orgOf())
      const target = all.filter((r) => this.filters.every((f) => matchOne(r, f)))
      for (const row of target) {
        await cfgWrite({ op: 'delete', tbl: this.tbl, org: this.orgOf(row), id: this.rowIdOf(row) })
      }
      return { data: null as unknown as T, error: null }
    } catch (e) {
      return { data: null as unknown as T, error: { message: e instanceof Error ? e.message : String(e) } }
    }
  }

  then<R1 = PgResult<T>, R2 = never>(
    onfulfilled?: ((value: PgResult<T>) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.run().then(onfulfilled, onrejected)
  }
}

// ── The shim client ──────────────────────────────────────────────────────────

function notSupported(area: string): never {
  throw new Error(`[cfg-store] supabase.${area} is not available in the S/4-hosted build (in-boundary config store only). If a feature needs it, add an S/4 backend.`)
}

export interface SupabaseLike {
  from(tbl: string): CfgQuery
  rpc(...args: unknown[]): never
  auth: Record<string, never>
  storage: Record<string, never>
  channel(...args: unknown[]): never
  removeChannel(...args: unknown[]): void
}

/**
 * Tables SAP owns once a timesheet is on SAP CATS: the shift master and the
 * shop floor clock. The hosted app (an organization on SAP, migration 00593)
 * keeps its own database for everything else and sends only these to SAP.
 */
export const SAP_OWNED_TABLES: ReadonlySet<string> = new Set([SHIFT_TABLE, ...CLOCK_TABLES])

/** A query on one SAP-owned table, served from SAP (hosted app on SAP CATS). */
export function sapOwnedTableQuery(tbl: string, legacyShifts?: () => Promise<Record<string, unknown>[]>): CfgQuery {
  return new CfgQuery(tbl, legacyShifts)
}

/** Build the in-boundary shim used in place of the real Supabase client. */
export function createCatsSupabaseShim(): SupabaseLike {
  return {
    from: (tbl: string) => new CfgQuery(tbl),
    rpc: () => notSupported('rpc'),
    get auth() { return notSupported('auth') },
    get storage() { return notSupported('storage') },
    channel: () => notSupported('channel'),
    removeChannel: () => { /* no realtime in-boundary; ignore */ },
  }
}
